'use client';

import { forwardRef } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 
  'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration'
> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'signup';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  fullWidth?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    variant = 'primary', 
    size = 'md', 
    icon, 
    iconPosition = 'left',
    loading = false,
    fullWidth = false,
    className = '',
    children,
    disabled,
    ...props 
  }, ref) => {
    const baseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
    
    const sizeClasses = {
      sm: 'px-3 py-2 text-button-sm',
      md: 'px-4 py-2 text-button-md',
      lg: 'px-6 py-3 text-button-lg'
    };

    const variantClasses = {
      primary: 'text-white hover:bg-[#203F72] focus:ring-[#4670BF]',
      secondary: 'bg-[#5A667A] text-white hover:bg-[#21272A] focus:ring-[#5A667A]',
      outline: 'border-2 border-[#4670BF] text-[#4670BF] hover:bg-[#4670BF] hover:text-white focus:ring-[#4670BF]',
      ghost: 'text-[#4670BF] hover:bg-[#B5CEFC] focus:ring-[#4670BF]',
      danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
      signup: 'text-[#4670BF] hover:bg-[#B5CEFC] focus:ring-[#4670BF]'
    };

    const getButtonStyle = () => {
      if (variant === 'primary') {
        return {
          borderRadius: '65px',
          background: '#4670BF',
          boxShadow: '0 1.333px 2.667px 0 rgba(0, 0, 0, 0.05)'
        };
      }
      if (variant === 'signup') {
        return {
          display: 'flex',
          height: '50px',
          padding: '16px 20px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '6px',
          borderRadius: '40px',
          background: '#FFF'
        };
      }
      return {};
    };

    const widthClasses = fullWidth ? 'w-full' : '';

    const buttonClasses = `
      ${baseClasses}
      ${sizeClasses[size]}
      ${variantClasses[variant]}
      ${widthClasses}
      ${className}
    `.trim();

    return (
      <motion.button
        ref={ref}
        className={buttonClasses}
        style={getButtonStyle()}
        disabled={disabled || loading}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        {...props}
      >
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mr-2"
          >
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </motion.div>
        )}
        
        {icon && iconPosition === 'left' && !loading && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="mr-2"
          >
            {icon}
          </motion.div>
        )}
        
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {children}
        </motion.span>
        
        {icon && iconPosition === 'right' && !loading && (
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="ml-2"
          >
            {icon}
          </motion.div>
        )}
      </motion.button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
